/* eslint-disable */

import moment from "moment";

// Colorado Public Utilities Commission - TNC Annual Inspection Report.
// Shared, identical layout for both Uber and Lyft in Colorado.
//
// `coloradoInspectionColumns` is the single source of truth for the inspection
// points so the HTML preview and the generated PDF stay in sync.
//
// Row types:
//  - "checkbox": main point or sub-point that renders PASS / FAIL boxes.
//  - "line":     a fillable measurement row (label + optional suffix + blank line).
//  - "tire":     handled separately via the `tires` array on point 17.

export const coloradoInspectionColumns = [
  // ---- Left column (points 1 - 8) ----
  [
    {
      number: 1,
      label: "Foot brakes (pads/shoes thickness)",
      type: "checkbox",
      aliases: ["foot brake", "foot brakes", "brake shoes", "brakes shoes", "pads shoes"],
      subRows: [
        { label: "Min. per manufacturer", type: "line", value: "3mm" },
        { label: "Right foot", suffix: "Measurements", type: "line", value: "7mm" },
        { label: "Left foot", suffix: "Measurements", type: "line", value: "7mm" },
        { label: "Right rear", suffix: "Measurements", type: "line", value: "7mm" },
        { label: "Left rear", suffix: "Measurements", type: "line", value: "7mm" },
      ],
    },
    {
      number: 2,
      label: "Emergency brake (parking brake)",
      type: "checkbox",
      aliases: ["emergency brake", "parking brake"],
    },
    {
      number: 3,
      label: "Steering mechanism",
      type: "checkbox",
      aliases: ["steering mechanism", "steering"],
      subRows: [
        { label: "Ball joints", type: "checkbox", aliases: ["ball joint"] },
        { label: "Tie rods", type: "checkbox", aliases: ["tie rod"] },
        { label: "Rack & pinion", type: "checkbox", aliases: ["rack and pinion", "rack pinion", "pinion"] },
        { label: "Bushings", type: "checkbox", aliases: ["bushing"] },
      ],
    },
    {
      number: 4,
      label: "Windshield",
      type: "checkbox",
      aliases: ["windshield"],
      subRows: [
        { label: "Large crack", type: "checkbox", aliases: ["large crack"] },
        { label: "Small crack", type: "checkbox", aliases: ["small crack"] },
      ],
    },
    {
      number: 5,
      label: "Rear window and other glass",
      type: "checkbox",
      aliases: ["rear window", "other glass"],
    },
    {
      number: 6,
      label: "Windshield wipers",
      type: "checkbox",
      aliases: ["windshield wiper", "wiper", "wipers"],
    },
    {
      number: 7,
      label: "Headlights",
      type: "checkbox",
      aliases: ["headlight", "headlights"],
    },
    {
      number: 8,
      label: "Tail lights",
      type: "checkbox",
      aliases: ["tail light", "tail lights"],
    },
  ],
  // ---- Right column (points 9 - 19) ----
  [
    {
      number: 9,
      label: "Turn indicator lights",
      type: "checkbox",
      aliases: ["turn indicator", "turn signal", "indicator lights"],
    },
    {
      number: 10,
      label: "Stop Lights",
      type: "checkbox",
      aliases: ["stop light", "brake light", "brake lights"],
    },
    {
      number: 11,
      label: "Front seat adjustment",
      type: "checkbox",
      aliases: ["front seat adjustment", "seat adjustment"],
    },
    {
      number: 12,
      label: "Doors (open, close, lock)",
      type: "checkbox",
      aliases: ["door", "doors"],
    },
    {
      number: 13,
      label: "Horn",
      type: "checkbox",
      aliases: ["horn"],
    },
    {
      number: 14,
      label: "Speedometer",
      type: "checkbox",
      aliases: ["speedometer"],
    },
    {
      number: 15,
      label: "Bumpers",
      type: "checkbox",
      aliases: ["bumper", "bumpers"],
    },
    {
      number: 16,
      label: "Muffler and exhaust system",
      type: "checkbox",
      aliases: ["muffler", "exhaust"],
    },
    {
      number: 17,
      label: "Tires, incl. tread depth",
      type: "checkbox",
      aliases: ["tire", "tires", "tread depth"],
      tires: [
        { label: "Right front", category: "right_front_tire" },
        { label: "Left front", category: "left_front_tire" },
        { label: "Right rear", category: "right_rear_tire" },
        { label: "Left rear", category: "left_rear_tire" },
      ],
    },
    {
      number: 18,
      label: "Interior and exterior rear view mirrors",
      type: "checkbox",
      aliases: [
        "interior and exterior rear view mirrors",
        "interior exterior rear view mirrors",
        "rear view mirror",
        "rear view mirrors",
        "rearview mirror",
        "rearview mirrors",
        "mirrors",
      ],
    },
    {
      number: 19,
      label: "Safety belts for driver and passanger(s)",
      type: "checkbox",
      aliases: ["safety belt", "seat belt"],
    },
  ],
];

export const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Returns "pass" | "fail" | null for a point given its aliases.
export const getCheckStatus = (checks = [], aliases = []) => {
  if (!aliases || aliases.length === 0) return null;
  const normalizedAliases = aliases.map(normalize);
  const matchedCheck = (checks || []).find((check) => {
    const checkName = normalize(check?.Check?.name || check?.name);
    if (!checkName) return false;
    return normalizedAliases.some(
      (alias) => alias && (checkName.includes(alias) || alias.includes(checkName))
    );
  });

  if (!matchedCheck) return null;
  const status = String(matchedCheck.status).toLowerCase();
  return matchedCheck.status === true ||
    matchedCheck.status === 1 ||
    status === "true" ||
    status === "1" ||
    status === "pass"
    ? "pass"
    : "fail";
};

// Tire tread depth pulled from inspection files by category.
export const getTireDimension = (reportData, category) => {
  const files = reportData?.Files || [];
  const match = files.find(
    (file) => file?.category === category || file?.category === "tire"
  );
  if (!match || match?.dimension === null || match?.dimension === undefined) {
    return "";
  }
  return match.dimension;
};

export const formatDate = (value, format = "MM/DD/YYYY") =>
  value ? moment(value).format(format) : "";

export const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

export const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim() ||
  getFullName(data);

// Hand-drawn tick variants so checked boxes look human, not stamped.
export const humanTickPaths = [
  "M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4",
  "M3.5 13.2C5.9 14.9 7.6 17.1 9.5 19.2C12.2 13.2 16.6 7.7 20.6 5.2",
  "M4.2 11.8C6.7 14.1 8.3 16.6 10.1 18.1C13.4 12.2 16.7 8.1 21.4 4.8",
  "M3.9 12.9C6 15.2 7.8 16.7 9.7 18.8C12.3 13.6 15.9 7.9 20.8 5",
  "M4.1 13.4C6.5 15 8.5 17.8 9.6 18.9C12.7 12.9 16.9 8.2 21.2 4.7",
];

// Hand-drawn ellipse for circling the PASS / FAIL result.
export const handDrawnCirclePath =
  "M52.1927 2.90815C34.1317 2.90815 15.3734 8.31886 6.56927 25.4371C3.77005 30.8798 1.5 32.7484 1.5 38.8919C1.5 44.4471 5.2551 50.3534 10.1902 53.2854C14.6663 55.9447 15.5908 61.4646 20.3287 63.7676C25.118 66.0956 26.9011 68.1651 32.5593 69.1652C42.084 70.8488 57.582 73.3293 66.6763 69.3999C70.4649 67.7629 77.2718 68.2962 81.4818 67.366C84.6931 66.6565 103.112 62.3328 104.253 58.4483C106.691 50.1537 115.056 36.3085 107.23 28.2532C102.352 23.2319 97.9017 18.6987 91.2985 15.5807C86.0695 13.1115 82.0045 12.4543 76.8149 10.6525C70.3868 8.42063 63.9661 4.31621 57.262 4.31621C51.9098 4.31621 47.9811 4.76555 43.5025 1.50009";
